import { useCallback, useEffect, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { LegendKey } from "../../../models/chart_model";
import { getHaryanaDemandMetrics } from "../../../Rest_api/RestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { H_CHART_TYPES } from "../const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";

import { AddDatesWithFileName } from "../../../helpers/helper";
import MetricChip from "../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { DiscomLoadMetrics } from "../../../models/metrics";

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" }
];

const UNITS = ["MW", "MWh", "LU"];

interface HaryanaSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  metrics?: DiscomLoadMetrics | null;
  metricsLoading?: boolean;
}

export function HaryanaSummaryAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  metrics,
  metricsLoading
}: HaryanaSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [loading, setLoading] = useState(true);
  // Track which legends are selected in the chart (empty = all selected)
  const [activeLegends, setActiveLegends] = useState<LegendKey[]>([]);

  const fetchSummaryData = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultTrendDates) {
      return;
    }
    setLoading(true);

    try {
      const promises = DefaultDates[activeTab].defaultTrendDates.map(async (date: any) => {
        try {
          const [startDate, endDate] = date.value.split(",");
          const data = await getHaryanaDemandMetrics(startDate, endDate);
          return data.map((item: any) => ({
            ...item,
            dates: date.label,
          }));
        } catch (error) {
          console.error(`Error processing date ${date.label}:`, error);
          return [];
        }
      });

      const results = await Promise.all(promises);
      const newData = results.flat();
      setSummaryData(newData);
    } catch (error) {
      console.error("Error fetching summary data:", error);
      setSummaryData([]);
    }

    setLoading(false);
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  useEffect(() => {
    const processedData = summaryData.map((item) => {
      const conversionFactor = selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

      return {
        slot: item.slot,
        slot_number: item.slot,
        slot_with_time: item.slot && item.time_string ? `${item.slot} (${item.time_string})` : item.slot,
        min_demand: item.min_demand * conversionFactor,
        max_demand: item.max_demand * conversionFactor,
        avg_demand: item.avg_demand * conversionFactor,
        mode_demand_window_formula: item.mode_demand_window_formula * conversionFactor,
        date_of_min: item.date_of_min,
        date_of_max: item.date_of_max,
      };
    });

    setChartData(processedData);
    setTableData(processedData);
  }, [summaryData, selectedUnit]);

  const getLegends = () => {
    return [
      {
        dataKey: "min_demand",
        name: "Min Demand",
        stroke: ExchangeColors[0],
        type: selectedChartType.value,
        stackId: selectedChartType.stackId !== undefined ? "summary" : undefined,
      },
      {
        dataKey: "max_demand",
        name: "Max Demand",
        stroke: ExchangeColors[1],
        type: selectedChartType.value,
        stackId: selectedChartType.stackId !== undefined ? "summary" : undefined,
      },
      {
        dataKey: "avg_demand",
        name: "Average Demand",
        stroke: ExchangeColors[2],
        type: selectedChartType.value,
        stackId: selectedChartType.stackId !== undefined ? "summary" : undefined,
      },
      {
        dataKey: "mode_demand_window_formula",
        name: "Mode Demand",
        stroke: ExchangeColors[3],
        type: selectedChartType.value,
        stackId: selectedChartType.stackId !== undefined ? "summary" : undefined,
      },
    ];
  };

  const resolveTooltipName = useCallback(
    (seriesName: string, dataPoint: any) => {
      if (seriesName === "Min Demand" && dataPoint?.date_of_min) {
        return `Min Demand (${dataPoint.date_of_min})`;
      } else if (seriesName === "Max Demand" && dataPoint?.date_of_max) {
        return `Max Demand (${dataPoint.date_of_max})`;
      }
      return seriesName;
    },
    [],
  );

  // For summary/table: use only the selected (active) legends, or all if none selected
  const allLegends = getLegends();
  const visibleLegends =
    activeLegends.length > 0
      ? allLegends.filter((l) =>
        activeLegends.some((al: any) => al.dataKey === l.dataKey)
      )
      : allLegends;

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: tableData,
    defaultSortKey: "block",
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: ["slot_number", "date_of_min", "date_of_max"],
    extraLabels: ["Block", "Min Date", "Max Date"],
    filename: AddDatesWithFileName({
      fileName: "Haryana Load Summary",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">

          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              onUnitChange(item);
            }}
          />

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(H_CHART_TYPES[index]);
            }}
          />

          {/* Disabled Filters */}
          <FilterMenu
            title="Discom"
            items={["UHBVN", "DHBVN"]}
            selectedItems={[]}
            disabled={true}
          />

          <FilterMenu
            title="Group By"
            items={["Discom Load", "Total Discom Load", "HVPN Drawal"]}
            selectedItems={[]}
            disabled={true}
          />

          <FilterMenu
            title="Type"
            items={["Schedule", "Drawal", "Load Shedding", "Total Load"]}
            selectedItems={[]}
            disabled={true}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
         <div className="flex flex-wrap gap-4 my-0 justify-center">
                    <MetricChip
                      icon={<PeakIcon width={20} height={19} />}
                      items={[
                        {
                          label: "Peak Load",
                          value: metrics?.["Peak Load (MW)"]?.value ?? undefined,
                          unit: metrics?.["Peak Load (MW)"]?.unit ?? "",
                        },
                      ]}
                    />
                    <MetricChip
                      icon={<AvgIcon width={20} height={19} />}
                      items={[
                        {
                          label: "Average Load",
                          value: metrics?.["Average Load (MW)"]?.value ?? undefined,
                          unit: metrics?.["Average Load (MW)"]?.unit ?? "",
                        },
                      ]}
                    />
                    <MetricChip
                      icon={<MinIcon width={20} height={19} />}
                      items={[
                        {
                          label: "Minimum Load",
                          value: metrics?.["Minimum Load (MW)"]?.value ?? undefined,
                          unit: metrics?.["Minimum Load (MW)"]?.unit ?? "",
                        },
                      ]}
                    />
                    <MetricChip
                      icon={<TotalIcon width={20} height={19} />}
                      items={[
                        {
                          label: "Total Energy",
                          value: metrics?.["Total Energy (MU)"]?.value ?? undefined,
                          unit: metrics?.["Total Energy (MU)"]?.unit ?? "",
                        },
                      ]}
                    />
                  </div>
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-80">Haryana Load Summary ({selectedUnit})</h2>
                <div className="flex gap-x-6 justify-end">
                  <button
                    onClick={() => {
                      dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                      dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                      dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>


              {dataTab.label === "Chart" && (
                <ReMixChart
                  key={`${selectedChartType.value}-${selectedUnit}`}
                  data={chartData}
                  showBrush={undefined}
                  isTimeSlot={true}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={getLegends()}
                  customLegendOrder={getLegends().map((l: any) => l.name)}
                  xDataKey="slot"
                  secondXDataKey={undefined}
                  resolveDisplayName={resolveTooltipName}
                  defaultSelectedLegends={activeLegends}
                  onLegendSelect={(selected: LegendKey[]) => setActiveLegends(selected)}
                />
              )}

              {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>HPPC</p>}
              text=" "
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
